import React, { useState, useMemo } from 'react';
import MultiSelectDropdown from '../MultiSelectDropdown';

// --- ICONOS SVG ---
const IconChevronDown = ({ className = "w-3.5 h-3.5 text-slate-400" }) => (
    <svg className={`${className} transition-transform duration-200`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
);

const IconCash = () => (
    <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);

const IconShield = () => (
    <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
);

/**
 * FiltrosCobranzas - Submódulo modular de filtros para el módulo de Cobranza.
 * Maneja los filtros específicos de caja y gestión de deuda, y los filtros generales de aseguradora y ramo.
 */
const FiltrosCobranzas = ({
    opcionesFiltrosUnicas = {},
    multiFiltros = {},
    setMultiFiltros,
    activeDropdown,
    setActiveDropdown
}) => {
    // Control de acordeones locales
    const [seccionesAbiertas, setSeccionesAbiertas] = useState({
        cobranza: true,
        riesgo: true
    });

    const toggleSeccion = (seccion) => {
        setSeccionesAbiertas(prev => ({ ...prev, [seccion]: !prev[seccion] }));
    };

    // Desestructuración defensiva
    const {
        estadoGestionCobranza = [],
        quienCobro = [],
        quienInformo = [],
        estadoDeuda = [],
        formaPago = [],
        compania = [],
        riesgo = [],
        moneda = []
    } = opcionesFiltrosUnicas;

    // Contadores de badges activos
    const contadorCobranza = useMemo(() => {
        return (multiFiltros.estadoGestionCobranza?.length || 0) +
               (multiFiltros.quienCobro?.length || 0) +
               (multiFiltros.quienInformo?.length || 0) +
               (multiFiltros.estadoDeuda?.length || 0) +
               (multiFiltros.formaPago?.length || 0);
    }, [multiFiltros]);

    const contadorRiesgo = useMemo(() => {
        return (multiFiltros.compania?.length || 0) +
               (multiFiltros.riesgo?.length || 0) +
               (multiFiltros.moneda?.length || 0);
    }, [multiFiltros]);

    return (
        <div className="space-y-4 animate-fade-in">
            {/* 1. SECCIÓN PRINCIPAL: GESTIÓN DE COBRANZAS */}
            <div className="border border-slate-150 rounded-2xl bg-slate-50/50 shadow-sm relative">
                <button
                    type="button"
                    onClick={() => toggleSeccion('cobranza')}
                    className={`w-full p-3 px-4 flex justify-between items-center bg-white hover:bg-slate-50 transition-all ${
                        seccionesAbiertas.cobranza ? 'rounded-t-2xl border-b border-slate-100' : 'rounded-2xl'
                    }`}
                >
                    <div className="flex items-center gap-2">
                        <IconCash />
                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700">Gestión de Cobranzas</span>
                    </div>
                    <div className="flex items-center gap-2">
                        {contadorCobranza > 0 && (
                            <span className="text-[9px] px-2 py-0.5 bg-emerald-100 text-emerald-800 font-extrabold rounded-full">
                                {contadorCobranza}
                            </span>
                        )}
                        <IconChevronDown className={seccionesAbiertas.cobranza ? 'rotate-180' : ''} />
                    </div>
                </button>
                
                {seccionesAbiertas.cobranza && (
                    <div className="p-4 space-y-4 bg-white border-t border-slate-50 rounded-b-2xl animate-fade-in">
                        <MultiSelectDropdown 
                            label="Estado de Cobranza" 
                            options={estadoGestionCobranza} 
                            selectedValues={multiFiltros.estadoGestionCobranza || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, estadoGestionCobranza: vals }))} 
                            isOpen={activeDropdown === 'estadoGestionCobranza'}
                            onToggle={(open) => setActiveDropdown(open ? 'estadoGestionCobranza' : null)}
                        />
                        <MultiSelectDropdown 
                            label="Quién Cobró" 
                            options={quienCobro} 
                            selectedValues={multiFiltros.quienCobro || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, quienCobro: vals }))} 
                            isOpen={activeDropdown === 'quienCobro'}
                            onToggle={(open) => setActiveDropdown(open ? 'quienCobro' : null)}
                        />
                        <MultiSelectDropdown 
                            label="Quién Informó" 
                            options={quienInformo} 
                            selectedValues={multiFiltros.quienInformo || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, quienInformo: vals }))} 
                            isOpen={activeDropdown === 'quienInformo'}
                            onToggle={(open) => setActiveDropdown(open ? 'quienInformo' : null)}
                        />
                        <MultiSelectDropdown 
                            label="Estado de Deuda" 
                            options={estadoDeuda} 
                            selectedValues={multiFiltros.estadoDeuda || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, estadoDeuda: vals }))} 
                            isOpen={activeDropdown === 'estadoDeuda'}
                            onToggle={(open) => setActiveDropdown(open ? 'estadoDeuda' : null)}
                        />
                        <MultiSelectDropdown 
                            label="Forma de Pago" 
                            options={formaPago} 
                            selectedValues={multiFiltros.formaPago || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, formaPago: vals }))} 
                            isOpen={activeDropdown === 'formaPago'}
                            onToggle={(open) => setActiveDropdown(open ? 'formaPago' : null)}
                        />
                    </div>
                )}
            </div>

            {/* 2. SECCIÓN: COMPAÑÍA Y RAMO */}
            <div className="border border-slate-150 rounded-2xl bg-slate-50/50 shadow-sm relative">
                <button
                    type="button"
                    onClick={() => toggleSeccion('riesgo')}
                    className={`w-full p-3 px-4 flex justify-between items-center bg-white hover:bg-slate-50 transition-all ${
                        seccionesAbiertas.riesgo ? 'rounded-t-2xl border-b border-slate-100' : 'rounded-2xl'
                    }`}
                >
                    <div className="flex items-center gap-2">
                        <IconShield />
                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700">Compañía y Ramo</span>
                    </div>
                    <div className="flex items-center gap-2">
                        {contadorRiesgo > 0 && (
                            <span className="text-[9px] px-2 py-0.5 bg-indigo-100 text-indigo-800 font-extrabold rounded-full">
                                {contadorRiesgo}
                            </span>
                        )}
                        <IconChevronDown className={seccionesAbiertas.riesgo ? 'rotate-180' : ''} />
                    </div>
                </button>
                
                {seccionesAbiertas.riesgo && (
                    <div className="p-4 space-y-4 bg-white border-t border-slate-50 rounded-b-2xl animate-fade-in">
                        <MultiSelectDropdown 
                            label="Compañía Aseguradora" 
                            options={compania} 
                            selectedValues={multiFiltros.compania || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, compania: vals }))} 
                            isOpen={activeDropdown === 'compania'}
                            onToggle={(open) => setActiveDropdown(open ? 'compania' : null)}
                        />
                        <MultiSelectDropdown 
                            label="Ramo Principal" 
                            options={riesgo} 
                            selectedValues={multiFiltros.riesgo || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, riesgo: vals }))} 
                            isOpen={activeDropdown === 'riesgo'}
                            onToggle={(open) => setActiveDropdown(open ? 'riesgo' : null)}
                        />
                        <MultiSelectDropdown 
                            label="Moneda" 
                            options={moneda} 
                            selectedValues={multiFiltros.moneda || []} 
                            onChange={(vals) => setMultiFiltros(p => ({ ...p, moneda: vals }))} 
                            isOpen={activeDropdown === 'moneda'}
                            onToggle={(open) => setActiveDropdown(open ? 'moneda' : null)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default FiltrosCobranzas;